import React, { useState, MouseEvent } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import Input from "@/components/UI/input";
import Button from "@/components/UI/button";
import { Controller, useForm } from "react-hook-form";

const LoginForm = () => {
  const { t } = useTranslation("common");
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotFormOpen, setIsForgotFormOpen] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const router = useRouter();

  const onForgotPassword = async (data: any) => {
    setIsLoading(true);

    const { email } = data;

    axios
      .post(`${process.env.NEXT_PUBLIC_API_URL}/auth/forgot-password`, {
        email: email,
      })
      .then(() => {
        setIsEmailSent(true);
        setHasErrors(false);
        setIsLoading(false);
      })
      .catch(() => {
        setHasErrors(true);
        setIsLoading(false);
      });
  };

  const onSubmit = async (data: any) => {
    setIsLoading(true);

    const { email, password } = data;

    axios
      .post(`${process.env.NEXT_PUBLIC_API_URL}/auth/login`, {
        username: email,
        password,
      })
      .then((response) => {
        Cookies.set("token", response?.data?.token, {
          path: "/",
        });
        Cookies.set("refreshToken", response?.data?.refresh_token, {
          path: "/",
        });

        setIsLoading(false);

        router.push("/");
      })
      .catch((err) => {
        setHasErrors(true);
        setIsLoading(false);
        console.log("*err", err);
      });
  };

  const toggleForgotPassword = (event: MouseEvent) => {
    event.preventDefault();
    setHasErrors(false);
    setIsForgotFormOpen(true);
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    values: {
      email: "",
      password: "",
    },
  });

  return (
    <>
      {!isForgotFormOpen && (
        <>
          <Controller
            name="email"
            control={control}
            rules={{
              validate: {
                required: (value: any) => !!value?.length,
              },
            }}
            render={({ field: { ref, ...field } }: any) => (
              <div className="mb-4">
                <label>
                  {t("EMAIL")}
                  <Input type="text" {...field} />
                </label>

                {errors?.email?.type === "required" && (
                  <span className="text-red-500 text-sm">
                    {t("ENTER_YOUR_EMAIL")}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="password"
            control={control}
            rules={{
              validate: {
                required: (value: any) => !!value?.length,
              },
            }}
            render={({ field: { ref, ...field } }: any) => (
              <div className="mb-4">
                <label>
                  {t("PASSWORD")}
                  <Input type="password" {...field} />
                </label>

                {errors?.email?.type === "required" && (
                  <span className="text-red-500 text-sm">
                    {t("ENTER_YOUR_PASSWORD")}
                  </span>
                )}
              </div>
            )}
          />

          <button
            className="text-primary text-sm float-right -mt-2 mb-6"
            onClick={toggleForgotPassword}
          >
            {t("FORGOT_PASSWORD")}
          </button>

          <Button
            className="w-full my-6"
            isLoading={isLoading}
            onClick={handleSubmit((data) => onSubmit(data))}
          >
            {t("LOGIN")}
          </Button>
        </>
      )}

      {isForgotFormOpen && (
        <>
          <p className="mb-4">{t("INPUT_YOUR_EMAIL")}</p>
          <Controller
            name="email"
            control={control}
            rules={{
              validate: {
                required: (value: any) => !!value?.length,
              },
            }}
            render={({ field: { ref, ...field } }: any) => (
              <div className="mb-4">
                <label>
                  {t("EMAIL")}
                  <Input type="text" {...field} />
                </label>

                {errors?.email?.type === "required" && (
                  <span className="text-red-500 text-sm">
                    {t("ENTER_YOUR_EMAIL")}
                  </span>
                )}
              </div>
            )}
          />

          {isEmailSent && (
            <div className="text-sky-500">{t("RESET_LINK_SENT")}</div>
          )}

          {hasErrors && (
            <div className="text-red-600">{t("ERRORS.ERROR_OCCURRED")}</div>
          )}

          {!isEmailSent && (
            <Button
              className="w-full my-6"
              isLoading={isLoading}
              onClick={handleSubmit((data) => onForgotPassword(data))}
            >
              {t("CONTINUE")}
            </Button>
          )}

          <div className="text-center text-gray-500 mb-2">{t("OR")}</div>

          <button
            onClick={() => setIsForgotFormOpen(false)}
            className="text-center text-primary block w-full"
          >
            {t("BACK_TO_LOGIN")}
          </button>
        </>
      )}
    </>
  );
};

export default LoginForm;
